import type { SessionSongDescriptor } from "./joinBootstrap";
import type { DemoCommandOp } from "./oscilloscopeUtils";

export interface CommandArbitrationRow {
  readonly op: DemoCommandOp;
  readonly seq: number;
  readonly issuerId: string;
  readonly executeAtMs: number;
  readonly receivedAtEpochMs: number;
  readonly verdict: "winner" | "overwritten";
}

export interface ArbitrationInput {
  readonly op: DemoCommandOp;
  readonly seq: number;
  readonly issuerId: string;
  readonly executeAtMs: number;
}

function normalizeStemUrlsByRole(
  value: SessionSongDescriptor["stemUrlsByRole"],
): Record<string, string> | undefined {
  if (!value) {
    return undefined;
  }
  const normalized: Record<string, string> = {};
  for (const [role, url] of Object.entries(value)) {
    const roleKey = role.trim();
    const urlValue = typeof url === "string" ? url.trim() : "";
    if (roleKey.length === 0 || urlValue.length === 0) {
      continue;
    }
    normalized[roleKey] = urlValue;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function descriptorHash(descriptor: SessionSongDescriptor | null): string {
  if (descriptor === null) {
    return "none";
  }
  const stemUrls = normalizeStemUrlsByRole(descriptor.stemUrlsByRole);
  const stemPart =
    stemUrls === undefined
      ? ""
      : Object.entries(stemUrls)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([role, url]) => `${role}=${url}`)
          .join("|");
  const raw = `${descriptor.youtubeId}|${descriptor.title}|${descriptor.smamsUrl ?? ""}|${stemPart}`;
  let hash = 5381;
  for (let index = 0; index < raw.length; index += 1) {
    hash = (hash * 33) ^ raw.charCodeAt(index);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function applyLwwArbitrationRows(
  previous: readonly CommandArbitrationRow[],
  input: ArbitrationInput,
  nowEpochMs: number,
): readonly CommandArbitrationRow[] {
  const next = [...previous];
  for (let index = next.length - 1; index >= 0; index -= 1) {
    const row = next[index];
    if (row?.op !== input.op || row.verdict !== "winner") {
      continue;
    }
    if (row.seq !== input.seq) {
      next[index] = { ...row, verdict: "overwritten" };
    }
    break;
  }
  next.push({
    op: input.op,
    seq: input.seq,
    issuerId: input.issuerId,
    executeAtMs: input.executeAtMs,
    receivedAtEpochMs: nowEpochMs,
    verdict: "winner",
  });
  return next.slice(-24);
}
